import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import AccessRequestModal from './AccessRequestModal';
import CreateAccountModal from './onboarding/CreateAccountModal';
import CommonInvestorQuestions from './CommonInvestorQuestions';
import AdvisorResources from './AdvisorResources';
import FeaturesSection from './landing/FeaturesSection';
import HowEncryptedSection from './landing/HowEncryptedSection';
import LandingFooter from './landing/LandingFooter';
import { LandingHeader, HeroSection } from './landing/LandingHero';
import { isBetaActive } from '../utils/betaMode';
import SEO from './SEO';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const [searchParams] = useSearchParams();
  const introId = searchParams.get('intro');
  const inviteParam = searchParams.get('invite');
  const prefillToken = searchParams.get('prefill');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showAccessRequestModal, setShowAccessRequestModal] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [introductionData, setIntroductionData] = useState(null);
  // Signed-in-user tier pricing for the two hero cards. Anonymous visitors
  // keep pricing === null and see the cards price-silent — matching Session
  // 281's landing-page-is-price-silent-for-prospects rule.
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        // No token → user is logged out. Skip the call entirely to avoid a
        // gratuitous 401 in every anonymous visitor's console and an unneeded
        // round-trip to the backend.
        if (!token) { setIsLoggedIn(false); return; }
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsLoggedIn(response.ok);
        // Fetch the signed-in user's tier pricing so the hero cards show
        // their country's fee. 204 (no country / country not in v3 config)
        // falls back to null → cards render price-silent. Zero regression
        // for anonymous visitors — the outer `if (!token) return` above
        // means we never even attempt the call for them.
        if (response.ok) {
          const p = await fetch(`${BACKEND_URL}/api/pricing/for-me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (p.status === 200) setPricing(await p.json());
        }
      } catch (error) { setIsLoggedIn(false); }
      finally { setIsChecking(false); }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    if (introId && !isChecking) {
      fetch(`${BACKEND_URL}/api/introduction/${introId}`).then(r => r.ok ? r.json() : null).then(data => {
        if (data) { setIntroductionData(data); setShowAccessRequestModal(true); }
      }).catch(err => logger.error('Error fetching introduction:', err));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introId, isChecking]);

  // Open the Invitation modal when /?invite=1 OR /?prefill=<token> is set
  useEffect(() => {
    if ((inviteParam || prefillToken) && !isChecking && !isLoggedIn) {
      setShowAccessRequestModal(true);
    }
  }, [inviteParam, prefillToken, isChecking, isLoggedIn]);

  const handleGetStarted = () => { if (isLoggedIn) window.location.href = '/dashboard'; else setShowCreateAccountModal(true); };

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100">
      <SEO
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'MyInvestFolio',
          url: 'https://beta.myinvestfolio.io',
          logo: 'https://beta.myinvestfolio.io/og/default.png',
          description: 'Independent, encrypted portfolio infrastructure for discerning investors. Multi-asset, multi-currency, multi-jurisdiction — without intermediaries.',
          sameAs: ['https://www.linkedin.com/company/myinvestfolio/'],
        }}
      />
      <LandingHeader isLoggedIn={isLoggedIn} isChecking={isChecking} handleGetStarted={handleGetStarted} setShowAccessRequestModal={setShowAccessRequestModal} />
      {!isLoggedIn && !isChecking && (
        <div className="bg-emerald-600 text-white">
          <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
            <div className="flex items-center gap-2"><span className="bg-white/20 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Beta</span><span className="text-sm sm:text-base font-semibold">Complimentary Beta Access</span></div>
            <span className="text-xs sm:text-sm text-emerald-100">Join our 3-month beta program and enjoy 6 months of free Premium access after.</span>
            <Button data-testid="beta-banner-join-btn" onClick={() => window.location.href = '/login?tab=register'} size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold text-xs px-4 py-1 h-auto whitespace-nowrap">Join Beta</Button>
          </div>
        </div>
      )}
      <AccessRequestModal open={showAccessRequestModal} onClose={() => { setShowAccessRequestModal(false); setIntroductionData(null); }} introductionData={introductionData} prefillToken={prefillToken} />
      <CreateAccountModal open={showCreateAccountModal} onClose={() => setShowCreateAccountModal(false)} onSuccess={() => { window.location.href = '/dashboard'; }} />
      <HeroSection isLoggedIn={isLoggedIn} isChecking={isChecking} handleGetStarted={handleGetStarted} handleRequestInvitation={() => setShowAccessRequestModal(true)} pricing={pricing} />
      <FeaturesSection />
      <HowEncryptedSection />
      <CommonInvestorQuestions />
      <AdvisorResources />
      <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-24">
        <div className="bg-slate-900 rounded-2xl sm:rounded-3xl p-6 sm:p-12 lg:p-16 text-center">
          <div className="space-y-4 sm:space-y-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-white">Two ways in. Both encrypted. Both yours.</h2>
            <p className="text-base sm:text-xl text-slate-300 max-w-2xl mx-auto">Start with Continuity System in a few minutes, or request an invitation to Portfolio Clarity for the full analytical view.</p>
            <div className="flex flex-col items-center gap-3">
              {isBetaActive() ? (
                <>
                  <Button
                    data-testid="cta-join-beta-btn"
                    onClick={() => (window.location.href = '/login?tab=register')}
                    size="lg"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-12 py-4 rounded-full font-medium shadow-lg hover:shadow-xl transition-all"
                  >
                    Join Beta<ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <p
                    className="text-xs text-slate-400 mt-1"
                    data-testid="cta-post-beta-note"
                  >
                    Self-serve signup and Portfolio Clarity invitations open after the beta.
                  </p>
                </>
              ) : (
                <>
                  <Button data-testid="cta-get-started-btn" onClick={handleGetStarted} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-12 py-4 rounded-full font-medium shadow-lg hover:shadow-xl transition-all">Create Account<ArrowRight className="ml-2 w-5 h-5" /></Button>
                  <button
                    type="button"
                    onClick={() => setShowAccessRequestModal(true)}
                    data-testid="cta-request-premium-invitation-link"
                    className="text-sm text-slate-300 hover:text-white underline underline-offset-4"
                  >
                    Or request a Portfolio Clarity invitation &rarr;
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <LandingFooter />
    </div>
  );
};

export default LandingPage;
